// Copyright (c) Ping. All rights reserved.
//
// Create Company — full-page 3-step flow.
// Figma: 1668-55810 (entry), 1694-21443 (step 1), 1753-19400 (step 2), 1753-19449 (step 3).
//
// Step 1/3  Company basics:  name · industry · location
// Step 2/3  Company details: logo · website · about
// Step 3/3  Invite team:     search contacts · invite link
//
// POST /v1/directory/companies (after step 1)
// All colour via design-token classes; arbitrary hex only where no token matches.

import { useCallback, useEffect, useRef, useState } from 'react';

import { Check, ChevronDown, Copy, Link2, Loader2, MapPin, Search, Upload, X } from 'lucide-react';

import {
  type AvatarContentType,
  requestAvatarUploadUrl,
  uploadAvatarBytes,
} from '@features/settings/web/profilesApi';

import type { Category, Company } from './directoryApi';
import { createCompany, getCategories } from './directoryApi';
import { persistCompanyOptimistically } from './useBusinessDirectory';

/**
 * Upload a company logo to a media blob (reusing the avatar presign pipeline)
 * and return a stable `media:<id>` reference to store on the company. Resolved
 * to a presigned URL on render, so it never expires.
 */
async function uploadCompanyLogo(file: File): Promise<string> {
  const contentType: AvatarContentType =
    file.type === 'image/webp'
      ? 'image/webp'
      : file.type === 'image/png'
        ? 'image/png'
        : 'image/jpeg';
  const upload = await requestAvatarUploadUrl(contentType);
  await uploadAvatarBytes(upload, file);
  return `media:${upload.blobKey}`;
}

// ─── Props ─────────────────────────────────────────────────────────────────────

export interface CreateCompanyPageProps {
  onCreated: (company: Company) => void;
  onCancel: () => void;
}

// ─── Reusable primitives (web-only, pure Tailwind) ────────────────────────────

function FormLabel({ children, required }: { children: string; required?: boolean }) {
  return (
    <p className="text-[12px] font-medium leading-5 text-light-text-primary dark:text-[#EBEBEB] m-0">
      {children}
      {required && <span className="text-light-accent-brand dark:text-dark-accent-brand ml-0.5">*</span>}
    </p>
  );
}

function FormInput({
  placeholder,
  value,
  onChange,
  type = 'text',
  leadingIcon,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'url' | 'email';
  leadingIcon?: React.ReactNode;
}) {
  return (
    <div className="relative">
      {leadingIcon != null && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-light-text-tertiary dark:text-dark-text-tertiary">
          {leadingIcon}
        </span>
      )}
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`
          w-full h-[41px] rounded-[14px] text-[12px] font-normal
          text-light-text-primary dark:text-[#FAFAFA]
          bg-light-surface-secondary dark:bg-dark-surface-primary
          border border-light-border-subtle dark:border-dark-border-strong outline-none
          placeholder:text-light-text-tertiary dark:placeholder:text-dark-text-tertiary
          focus:border-light-accent-brand/50 dark:focus:border-dark-accent-brand/50
          transition-colors
          ${leadingIcon != null ? 'pl-10 pr-4' : 'px-4'}
        `}
      />
    </div>
  );
}

function FormTextarea({
  placeholder,
  value,
  onChange,
  rows = 4,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <textarea
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="
        w-full rounded-[14px] text-[12px] font-normal px-4 py-3
        text-light-text-primary dark:text-[#FAFAFA]
        bg-light-surface-secondary dark:bg-dark-surface-primary
        border border-light-border-subtle dark:border-dark-border-strong outline-none resize-none
        placeholder:text-light-text-tertiary dark:placeholder:text-dark-text-tertiary
        focus:border-light-accent-brand/50 dark:focus:border-dark-accent-brand/50
        transition-colors
      "
    />
  );
}

interface DropdownProps<T extends string> {
  value: T | '';
  options: { label: string; value: T }[];
  placeholder: string;
  onChange: (v: T | '') => void;
}

function Dropdown<T extends string>({ value, options, placeholder, onChange }: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="
          w-full h-[41px] flex items-center justify-between px-4 rounded-[14px]
          text-[12px] font-normal text-left
          bg-light-surface-secondary dark:bg-dark-surface-primary
          border border-light-border-subtle dark:border-dark-border-strong
          text-light-text-primary dark:text-[#FAFAFA]
          focus:outline-none focus:border-light-accent-brand/50 dark:focus:border-dark-accent-brand/50
          transition-colors
        "
      >
        <span className={selected ? '' : 'text-light-text-tertiary dark:text-dark-text-tertiary'}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown size={14} className="text-light-text-tertiary dark:text-dark-text-tertiary flex-shrink-0" />
      </button>

      {open && (
        <div className="
          absolute z-20 left-0 right-0 mt-1 rounded-xl overflow-hidden shadow-2xl
          bg-light-surface-primary dark:bg-dark-surface-elevated
          border border-light-border-subtle dark:border-dark-border-strong
        ">
          {value !== '' && (
            <button
              type="button"
              onClick={() => { onChange('' as T | ''); setOpen(false); }}
              className="w-full flex items-center px-4 h-9 text-[12px] text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-surface-secondary dark:hover:bg-dark-surface-secondary"
            >
              Clear selection
            </button>
          )}
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`
                w-full flex items-center justify-between px-4 h-9 text-[12px] text-left
                hover:bg-light-surface-secondary dark:hover:bg-dark-surface-secondary
                ${opt.value === value
                  ? 'text-light-accent-brand dark:text-dark-accent-brand font-medium'
                  : 'text-light-text-primary dark:text-[#FAFAFA]'}
              `}
            >
              {opt.label}
              {opt.value === value && <Check size={12} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Step indicator ─────────────────────────────────────────────────────────────

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[12px] font-normal text-light-text-tertiary dark:text-[#B3B3B3] m-0">
        Step {current}/{total}
      </p>
      <div className="flex gap-1.5">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`h-[3px] flex-1 rounded-full transition-colors ${
              i < current
                ? 'bg-light-accent-brand dark:bg-dark-accent-brand'
                : 'bg-light-surface-elevated dark:bg-dark-surface-elevated'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Footer buttons ─────────────────────────────────────────────────────────────

function FormFooter({
  onBack,
  onCancel,
  onNext,
  nextLabel,
  nextDisabled,
  nextBusy,
  showBack,
}: {
  onBack?: () => void;
  onCancel: () => void;
  onNext: () => void;
  nextLabel: string;
  nextDisabled?: boolean;
  nextBusy?: boolean;
  showBack: boolean;
}) {
  return (
    <div className="flex items-center justify-between mt-8">
      {showBack && onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="
            h-[39px] px-5 rounded-xl text-[16px] font-semibold
            text-light-text-primary dark:text-[#FFFFFF]
            bg-light-surface-secondary dark:bg-dark-surface-elevated
            hover:bg-light-surface-tertiary dark:hover:bg-dark-surface-secondary
            transition-colors
          "
        >
          Back
        </button>
      ) : (
        <div />
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="
            h-[39px] px-5 rounded-xl text-[16px] font-semibold
            text-light-text-primary dark:text-[#FFFFFF]
            bg-light-surface-secondary dark:bg-dark-surface-elevated
            hover:bg-light-surface-tertiary dark:hover:bg-dark-surface-secondary
            transition-colors
          "
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={nextDisabled === true || nextBusy === true}
          className="
            h-[39px] px-5 rounded-xl text-[16px] font-semibold text-white
            bg-light-accent-brand dark:bg-dark-accent-brand
            hover:opacity-90 disabled:opacity-50 transition-opacity
            flex items-center gap-2
          "
        >
          {nextBusy === true && <Loader2 size={15} className="animate-spin" />}
          {nextLabel}
        </button>
      </div>
    </div>
  );
}

// ─── Step 1: Company basics ──────────────────────────────────────────────────────

export interface Step1Data {
  name: string;
  industrySlug: string;
  location: string;
}

function Step1({
  categories,
  onNext,
  onCancel,
}: {
  categories: readonly Category[];
  onNext: (data: Step1Data) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [industrySlug, setIndustrySlug] = useState<string>('');
  const [location, setLocation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const industryOpts = categories.map((c) => ({ label: c.name, value: c.slug }));

  const handleNext = useCallback(() => {
    if (name.trim().length === 0) {
      setError('Company name is required.');
      return;
    }
    setError(null);
    onNext({ name: name.trim(), industrySlug, location });
  }, [name, industrySlug, location, onNext]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-[6px]">
        <FormLabel required>Company name</FormLabel>
        <FormInput placeholder="e.g., Acme Studios" value={name} onChange={setName} />
      </div>

      <div className="flex flex-col gap-[6px]">
        <FormLabel required>Industry / Niche</FormLabel>
        <Dropdown
          value={industrySlug as never}
          options={industryOpts}
          placeholder="Select industry"
          onChange={(v) => setIndustrySlug(v)}
        />
      </div>

      <div className="flex flex-col gap-[6px]">
        <FormLabel>Location (optional)</FormLabel>
        <FormInput
          placeholder="San Francisco, CA"
          value={location}
          onChange={setLocation}
          leadingIcon={<MapPin size={16} />}
        />
      </div>

      {error != null && (
        <p className="text-[12px] text-[#FF5353] m-0">{error}</p>
      )}

      <FormFooter
        showBack={false}
        onCancel={onCancel}
        onNext={handleNext}
        nextLabel="Continue"
        nextDisabled={name.trim().length === 0}
      />
    </div>
  );
}

// ─── Step 2: Company details ─────────────────────────────────────────────────────

function Step2({
  step1,
  onBack,
  onNext,
  onCancel,
}: {
  step1: Step1Data;
  onBack: () => void;
  onNext: (company: Company) => void;
  onCancel: () => void;
}) {
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleNext = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const parts = step1.location.trim().split(',').map((s) => s.trim());
      const locationCity = parts[0] ?? '';
      const locationCountry = parts[1] ?? '';
      // description is "optional" in the UI but the API requires a non-empty
      // value — fall back to the company name when the user leaves it blank.
      const resolvedDescription = description.trim() || step1.name;
      // Upload the picked logo to a media blob and bind it as a stable
      // `media:<id>` reference (resolved to a presigned URL on render, like
      // avatars) so it never expires. Best-effort: a logo upload failure must
      // not block company creation.
      let logoRef: string | undefined;
      if (logoFile !== null) {
        logoRef = await uploadCompanyLogo(logoFile).catch(() => undefined);
      }
      const company = await createCompany({
        name: step1.name,
        description: resolvedDescription,
        categorySlugs: step1.industrySlug ? [step1.industrySlug] : [],
        ...(website.trim() ? { website: website.trim() } : {}),
        ...(logoRef ? { logoUrl: logoRef } : {}),
        ...(locationCity ? { locationCity } : {}),
        ...(locationCountry ? { locationCountry } : {}),
      });
      // Persist to Dexie immediately so the sidebar reflects the new company.
      void persistCompanyOptimistically(company);
      onNext(company);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to create company.');
    } finally {
      setBusy(false);
    }
  }, [step1, website, description, logoFile, onNext]);

  return (
    <div className="flex flex-col gap-5">
      {/* Logo upload */}
      <div className="flex items-center gap-4">
        {logoPreview != null ? (
          <div className="relative w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
            <img src={logoPreview} alt="Company logo" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => setLogoPreview(null)}
              className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center"
            >
              <X size={10} />
            </button>
          </div>
        ) : null}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="
            flex items-center gap-2 h-[41px] px-5 rounded-full
            border border-light-border-strong dark:border-dark-border-strong
            text-[16px] font-medium text-light-text-primary dark:text-[#FFFFFF]
            hover:bg-light-surface-secondary dark:hover:bg-dark-surface-elevated
            transition-colors
          "
        >
          <Upload size={18} />
          Upload company logo
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleLogoChange}
        />
      </div>

      <div className="flex flex-col gap-[6px]">
        <FormLabel>Website (optional)</FormLabel>
        <FormInput
          type="url"
          placeholder="https://yourcompany.com"
          value={website}
          onChange={setWebsite}
        />
      </div>

      <div className="flex flex-col gap-[6px]">
        <FormLabel>About (optional)</FormLabel>
        <FormTextarea
          placeholder="e.g., We build innovative design tools that help teams collaborate better"
          value={description}
          onChange={setDescription}
          rows={4}
        />
        <p className="text-[10px] text-light-text-tertiary dark:text-dark-text-tertiary m-0">
          Keep it brief, 1–2 sentences
        </p>
      </div>

      {error != null && (
        <p className="text-[12px] text-[#FF5353] m-0">{error}</p>
      )}

      <FormFooter
        showBack
        onBack={onBack}
        onCancel={onCancel}
        onNext={handleNext}
        nextLabel="Continue"
        nextBusy={busy}
      />
    </div>
  );
}

// ─── Step 3: Invite team ──────────────────────────────────────────────────────────

function Step3({
  company,
  onBack,
  onDone,
  onCancel,
}: {
  company: Company;
  onBack: () => void;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  const inviteLink = `ping.app/join/${company.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}-${company.id.slice(0, 4)}`;

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(`https://${inviteLink}`);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="text-[12px] font-bold text-light-text-primary dark:text-[#FAFAFA] m-0 mb-1">
          Invite your team
        </p>
        <p className="text-[10px] text-light-text-tertiary dark:text-[#A1A1A1] m-0">
          Invite teammates so they can access this company inside Ping.
        </p>
      </div>

      {/* Search field */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-text-tertiary dark:text-dark-text-tertiary pointer-events-none" />
        <input
          type="search"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="
            w-full h-[41px] pl-9 pr-4 rounded-[14px] text-[12px]
            bg-light-surface-secondary dark:bg-dark-surface-primary
            text-light-text-primary dark:text-[#FAFAFA]
            placeholder:text-light-text-tertiary dark:placeholder:text-dark-text-tertiary
            border border-light-border-subtle dark:border-dark-border-strong outline-none
            focus:border-light-accent-brand/50 dark:focus:border-dark-accent-brand/50
            transition-colors
          "
        />
      </div>

      {/* Invite link panel */}
      <div className="rounded-xl bg-light-surface-secondary dark:bg-dark-surface-primary p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link2 size={14} className="text-light-text-secondary dark:text-[#AEAEB2]" />
            <p className="text-[12px] text-light-text-secondary dark:text-[#AEAEB2] m-0">
              Invite link
            </p>
          </div>
          <button
            type="button"
            onClick={handleCopyLink}
            className="
              flex items-center gap-1.5 h-[26px] px-3 rounded-lg text-[12px] font-medium
              bg-light-surface-elevated dark:bg-dark-surface-elevated
              text-light-text-primary dark:text-[#FAFAFA]
              hover:bg-light-surface-tertiary dark:hover:bg-dark-surface-secondary
              transition-colors
            "
          >
            {linkCopied ? <Check size={12} /> : <Copy size={12} />}
            {linkCopied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
        <div className="flex items-center h-9 px-3 rounded-lg bg-light-surface-primary dark:bg-dark-surface-elevated border border-light-border-subtle dark:border-dark-border-subtle">
          <p className="text-[12px] text-light-text-primary dark:text-[#EBEBEB] m-0 truncate">
            {inviteLink}
          </p>
        </div>
        <p className="text-[10px] text-light-text-tertiary dark:text-[#808080] m-0 border-t border-light-border-subtle dark:border-dark-border-subtle pt-3">
          Anyone with the link can join. This link is active for 24 hours.
        </p>
      </div>

      <FormFooter
        showBack
        onBack={onBack}
        onCancel={onCancel}
        onNext={onDone}
        nextLabel="Create company"
      />
    </div>
  );
}

// ─── Root page ─────────────────────────────────────────────────────────────────

export function CreateCompanyPage({ onCreated, onCancel }: CreateCompanyPageProps): JSX.Element {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [categories, setCategories] = useState<readonly Category[]>([]);
  // step1Data is collected in step 1 and passed to step 2 for the API call.
  const [step1Data, setStep1Data] = useState<Step1Data | null>(null);
  // company is created at the end of step 2 and used by step 3.
  const [company, setCompany] = useState<Company | null>(null);

  useEffect(() => {
    getCategories()
      .then((cats) => setCategories(cats))
      .catch(() => { /* non-fatal */ });
  }, []);

  return (
    <div className="flex-1 flex flex-col items-center justify-center min-h-0 overflow-y-auto bg-light-surface-app dark:bg-dark-surface-app px-4 py-12">
      <div className="w-full max-w-[622px]">
        {/* Header */}
        <div className="mb-8">
          <StepIndicator current={step} total={3} />
          <div className="mt-4">
            <h1 className="text-[20px] font-bold text-light-text-primary dark:text-[#EBEBEB] m-0">
              Create your company workspace
            </h1>
            <p className="text-[12px] text-light-text-tertiary dark:text-[#B3B3B3] m-0 mt-1">
              This should take less than a minute
            </p>
          </div>
        </div>

        {/* Steps */}
        {step === 1 && (
          <Step1
            categories={categories}
            onCancel={onCancel}
            onNext={(data) => {
              setStep1Data(data);
              setStep(2);
            }}
          />
        )}

        {step === 2 && step1Data != null && (
          <Step2
            step1={step1Data}
            onBack={() => setStep(1)}
            onCancel={onCancel}
            onNext={(c) => {
              setCompany(c);
              setStep(3);
            }}
          />
        )}

        {step === 3 && company != null && (
          <Step3
            company={company}
            onBack={() => setStep(2)}
            onCancel={onCancel}
            onDone={() => onCreated(company)}
          />
        )}
      </div>
    </div>
  );
}
